import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, X, Plus, Minus, ArrowRight } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";
import api from "@/api";

const inputCls = "w-full rounded-xl border border-clayline bg-cream px-4 py-3 text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:ring-2 focus:ring-terracotta/60";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [open, setOpen] = useState(false);
  const [customer, setCustomer] = useState({ name: "", phone: "", address: "", pincode: "", city: "", state: "" });
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/auth/google/me")
      .then((r) => setCustomer((c) => (c.name ? c : { ...c, name: r.data.name })))
      .catch(() => {});
  }, []);

  const items = useMemo(
    () => products.filter((p) => cart[p.id]).map((p) => ({ ...p, qty: cart[p.id] })),
    [cart, products]
  );
  const total = items.reduce((s, i) => s + i.price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  const add = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const sub = (id) => setCart((c) => {
    const q = (c[id] || 0) - 1;
    const n = { ...c };
    if (q <= 0) delete n[id]; else n[id] = q;
    return n;
  });

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    const payload = {
      customer,
      items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
      origin_url: window.location.origin,
    };
    try {
      const { data } = await api.post("/orders/razorpay", payload);
      const scriptOk = await loadRazorpayScript();
      if (!scriptOk) throw new Error("razorpay_script_failed");
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Chai Times",
        description: "Chai Times Shop Order",
        order_id: data.razorpay_order_id,
        prefill: { name: customer.name, contact: customer.phone },
        theme: { color: "#0C312A" },
        handler: async (resp) => {
          try {
            await api.post("/orders/razorpay/verify", { order_id: data.order_id, ...resp });
            window.location.href = "/payment/success?paid=1";
          } catch {
            toast.error("Payment verification failed. Contact us if money was deducted.");
            setPlacing(false);
          }
        },
        modal: { ondismiss: () => setPlacing(false) },
      });
      rzp.open();
    } catch (err) {
      if (err.response?.data?.detail === "razorpay_not_configured") {
        toast.error("Online payments are opening soon — call us to place your order.");
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
      setPlacing(false);
    }
  };

  return (
    <div data-testid="shop-page">
      <section className="bg-terracotta text-cream pt-36 md:pt-44 pb-20 relative grain">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-cream/70">Chai Times Shop</p>
            <h1 className="font-serif font-black text-5xl md:text-7xl tracking-tight mt-5 leading-[0.98]">
              Take the brew<br /><span className="italic">home with you.</span>
            </h1>
            <p className="mt-6 max-w-lg text-cream/85 leading-relaxed">
              Signature blends ground fresh every week, hand-thrown kulhads, and cutting-chai glasses — delivered across Bengaluru.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.06}>
              <div className="border border-clayline rounded-2xl overflow-hidden bg-cream group hover:shadow-xl transition-shadow duration-300" data-testid={`product-card-${i}`}>
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={p.image && p.image.startsWith("http") ? p.image : `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.image}`} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  {p.badge && (
                    <span className="absolute top-4 left-4 w-16 h-16 rounded-full bg-terracotta text-cream font-mono text-[10px] uppercase tracking-wider flex items-center justify-center text-center rotate-[-8deg] leading-tight">
                      {p.badge}
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-serif font-bold text-xl">{p.name}</h3>
                    <span className="font-mono font-bold text-terracotta whitespace-nowrap">₹{p.price}</span>
                  </div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/45 mt-1">{p.size}</p>
                  <p className="text-sm text-charcoal/60 mt-3 leading-relaxed">{p.description}</p>
                  <button
                    onClick={() => { add(p.id); setOpen(true); }}
                    data-testid={`product-add-${i}`}
                    className="mt-5 w-full rounded-full px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] border border-charcoal text-charcoal hover:bg-terracotta hover:text-cream hover:border-terracotta transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <ShoppingBag size={14} /> Add to Cart
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Floating cart button */}
      <AnimatePresence>
        {count > 0 && !open && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={() => setOpen(true)}
            data-testid="cart-open-btn"
            className="fixed bottom-6 right-6 z-40 rounded-full bg-charcoal text-cream px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] flex items-center gap-3 shadow-2xl hover:bg-terracotta transition-colors"
          >
            <ShoppingBag size={16} /> {count} item{count > 1 ? "s" : ""} — ₹{total}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Cart drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-charcoal/60 z-40"
              onClick={() => setOpen(false)}
              data-testid="cart-overlay"
            />
            <motion.aside
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-cream z-50 shadow-2xl flex flex-col"
              data-testid="cart-drawer"
            >
              <div className="flex items-center justify-between p-6 border-b border-clayline">
                <h2 className="font-serif font-bold text-2xl">Your Kulhad Cart</h2>
                <button onClick={() => setOpen(false)} data-testid="cart-close-btn" className="p-2 hover:text-terracotta transition-colors" aria-label="Close cart"><X size={22} /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                {items.length === 0 && <p className="text-charcoal/50 font-mono text-sm" data-testid="cart-empty">Your cart is empty.</p>}
                {items.map((i) => (
                  <div key={i.id} className="flex items-center gap-4" data-testid={`cart-item-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <img src={i.image && i.image.startsWith("http") ? i.image : `${process.env.REACT_APP_BACKEND_URL}/api/files/${i.image}`} alt="" className="w-14 h-14 rounded-xl object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold truncate">{i.name}</p>
                      <p className="font-mono text-sm text-terracotta">₹{i.price} × {i.qty}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => sub(i.id)} data-testid={`cart-sub-${i.id}`} className="w-7 h-7 rounded-full border border-clayline flex items-center justify-center hover:bg-sand"><Minus size={13} /></button>
                      <button onClick={() => add(i.id)} data-testid={`cart-add-${i.id}`} className="w-7 h-7 rounded-full border border-clayline flex items-center justify-center hover:bg-sand"><Plus size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
              {items.length > 0 && (
                <form onSubmit={placeOrder} className="p-6 border-t border-clayline space-y-3" data-testid="checkout-form">
                  <input required placeholder="Full name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className={inputCls} data-testid="checkout-name-input" />
                  <input required placeholder="Phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className={inputCls} data-testid="checkout-phone-input" />
                  <input type="email" placeholder="Email (for order confirmation)" value={customer.email || ""} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className={inputCls} data-testid="checkout-email-input" />
                  <textarea required rows={2} placeholder="Address (house / flat, street, area)" value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} className={inputCls} data-testid="checkout-address-input" />
                  <div className="grid grid-cols-3 gap-3">
                    <input required placeholder="Pincode" inputMode="numeric" value={customer.pincode} onChange={(e) => setCustomer({ ...customer, pincode: e.target.value })} className={inputCls} data-testid="checkout-pincode-input" />
                    <input required placeholder="City" value={customer.city} onChange={(e) => setCustomer({ ...customer, city: e.target.value })} className={inputCls} data-testid="checkout-city-input" />
                    <input required placeholder="State" value={customer.state} onChange={(e) => setCustomer({ ...customer, state: e.target.value })} className={inputCls} data-testid="checkout-state-input" />
                  </div>
                  <button type="submit" disabled={placing} data-testid="place-order-btn" className="w-full rounded-full px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] bg-terracotta text-cream hover:bg-charcoal transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                    {placing ? "Opening secure checkout…" : `Pay ₹${total} — Secure Checkout`} <ArrowRight size={14} />
                  </button>
                </form>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Shop;
